/**
 * Content Studio editing for the data-driven page families.
 *
 * PAWTENANT-CONTENT-STUDIO-COVERAGE-001
 *
 * Blog articles, the automated (scheduled) articles, the 51 ESA state pages,
 * the 51 PSD state pages and the college policy pages are each rendered by ONE
 * template from ONE record. Instead of re-wiring every template, each family
 * declares a schema here: which record fields an editor may change, with what
 * label and limits. The same schema
 *
 *   1. produces the page's section map for the studio (scripts/cms/inventory.mjs
 *      calls recordMapFor() with the compiled record, so the defaults the studio
 *      shows are exactly what the page renders today), and
 *   2. patches the record with the published overrides before the template,
 *      the listing cards and the prerendered <head> JSON-LD read it
 *      (cmsBlogPost, cmsScheduledArticle, cmsStateEsa, cmsStatePsd, cmsCollege).
 *
 * Because the template still renders the record exactly as before, a page with
 * no published override receives the ORIGINAL record object (identity) and
 * renders byte-identically. Fields the template does not render, prices,
 * dates, authors and credentials, categories (they drive the blog filters),
 * sources and slugs are not offered, or are listed as managed.
 *
 * New automated articles appear in the studio with no integration work: the
 * inventory enumerates SCHEDULED_ARTICLES and maps each through this schema.
 * Field ids are stable names (not positions), so an edit keeps applying after
 * later deployments.
 */

import { cmsOverrides } from "./bundle";
import { resolvePageContent } from "@/seo/content/runtime";
import type {
  SeoCollectionColumn,
  SeoCollectionKind,
  SeoEditableCollection,
  SeoEditableField,
  SeoFieldKind,
  SeoPageContentMap,
  SeoSectionType,
} from "@/seo/content/types";
import type { BlogPost } from "@/mocks/blogPosts";
import type { ScheduledArticle } from "@/seo/blog/scheduledArticles";
import type { StateData } from "@/mocks/states";
import type { StatePSDData } from "@/mocks/statesPSD";
import type { College } from "@/mocks/colleges";
import type { StatePetRentBlogConfig } from "@/components/feature/StatePetRentBlog";

// ── schema model ─────────────────────────────────────────────────────────────

interface FieldSpec<R> {
  id: string;
  label: string;
  kind: SeoFieldKind;
  where?: string;
  help?: string;
  get: (r: R) => string;
  /** Absent = managed (shown, never editable). */
  set?: (r: R, value: string) => R;
  when?: (r: R) => boolean;
}

interface ImageSpec<R> {
  id: string;
  altId: string;
  label: string;
  altLabel: string;
  get: (r: R) => { src: string; alt: string };
  set: (r: R, img: { src: string; alt: string }) => R;
  when?: (r: R) => boolean;
}

interface CollectionSpec<R> {
  id: string;
  label: string;
  kind: SeoCollectionKind;
  where?: string;
  help?: string;
  columns: SeoCollectionColumn[];
  get: (r: R) => Array<Record<string, string>>;
  set: (r: R, items: Array<Record<string, string>>) => R;
  /** Card sets whose design depends on the count: re-word only. */
  fixedLength?: boolean;
  minItems?: number;
  maxItems?: number;
  when?: (r: R) => boolean;
}

export interface RecordSchema<R> {
  family: string;
  sectionId: string;
  sectionLabel: string;
  sectionType: SeoSectionType;
  description: string;
  fields: FieldSpec<R>[];
  images: ImageSpec<R>[];
  collections: CollectionSpec<R>[];
  /** Collection ids that feed FAQPage structured data (FAQ parity). */
  faqCollections: string[];
}

const kindFor = (preferred: SeoFieldKind, value: string): SeoFieldKind =>
  preferred === "heading" && value.length > 120 ? "paragraph" : preferred;

/** The studio section map for one record (defaults = what the page renders today). */
export function recordMapFor<R>(schema: RecordSchema<R>, routePath: string, record: R): SeoPageContentMap {
  const fields: SeoEditableField[] = [];
  for (const f of schema.fields) {
    if (f.when && !f.when(record)) continue;
    const value = String(f.get(record) ?? "");
    fields.push({ id: f.id, label: f.label, kind: kindFor(f.kind, value), value, editable: !!f.set && value.length > 0, source: f.set ? "registry" : "managed_design", where: f.where, help: f.help });
  }
  for (const im of schema.images) {
    if (im.when && !im.when(record)) continue;
    const { src, alt } = im.get(record);
    fields.push({ id: im.id, label: im.label, kind: "image", value: src, editable: true, source: "registry", where: "Image shown on the page" });
    fields.push({ id: im.altId, label: im.altLabel, kind: "image_alt", value: alt, editable: alt.length > 0, source: "registry", help: "Describe the picture for screen readers and search engines." });
  }
  const collections: SeoEditableCollection[] = [];
  for (const c of schema.collections) {
    if (c.when && !c.when(record)) continue;
    const items = c.get(record).map((i) => Object.fromEntries(Object.entries(i).map(([k, v]) => [k, String(v ?? "")])));
    const fixed = !!c.fixedLength;
    collections.push({
      id: c.id, label: c.label, kind: c.kind, columns: c.columns, items,
      reorderable: !fixed, addable: !fixed, removable: !fixed,
      minItems: fixed ? items.length : (c.minItems ?? 1),
      maxItems: fixed ? items.length : Math.max(items.length, c.maxItems ?? 30),
      keyed: false, where: c.where, help: c.help,
    });
  }
  return {
    routePath,
    version: 1,
    sections: [{ id: schema.sectionId, label: schema.sectionLabel, order: 1, sectionType: schema.sectionType, description: schema.description, fields, collections, managed: false }],
  };
}

// Keyed by the overrides object first, so swapping the bundle (the guard's test
// seam) can never serve a stale patched record.
const cache = new WeakMap<object, WeakMap<object, Map<string, unknown>>>();

/**
 * The record with this route's published overrides applied. Returns the SAME
 * object when nothing is published for the route (no copy, no re-render
 * difference); only fields that carry an override are written.
 */
export function patchRecord<R extends object>(schema: RecordSchema<R>, routePath: string, record: R): R {
  const overrides = cmsOverrides(routePath);
  if (!overrides || !Object.keys(overrides).length) return record;
  let byRecord = cache.get(overrides);
  if (!byRecord) cache.set(overrides, (byRecord = new WeakMap()));
  let byRoute = byRecord.get(record);
  if (!byRoute) byRecord.set(record, (byRoute = new Map()));
  const key = `${schema.family}|${routePath}`;
  if (byRoute.has(key)) return byRoute.get(key) as R;
  const map = recordMapFor(schema, routePath, record);
  const c = resolvePageContent(map, overrides);
  let out: R = record;
  for (const f of schema.fields) {
    if (!f.set || (f.when && !f.when(record)) || !c.overridden(f.id)) continue;
    out = f.set(out, c.text(f.id));
  }
  for (const im of schema.images) {
    if (im.when && !im.when(record)) continue;
    if (!c.overridden(im.id) && !c.overridden(im.altId)) continue;
    const r = c.image(im.id, im.altId);
    out = im.set(out, { src: r.src, alt: r.alt });
  }
  for (const col of schema.collections) {
    if ((col.when && !col.when(record)) || !c.overridden(col.id)) continue;
    out = col.set(out, c.items(col.id));
  }
  byRoute.set(key, out);
  return out;
}

const col = (key: string, label: string, kind: SeoFieldKind): SeoCollectionColumn => ({ key, label, kind });
const splitParas = (body: string) => body.split(/\n\s*\n/).map((p) => p.replace(/\s+/g, " ").trim()).filter(Boolean);

// ── blog articles (src/mocks/blogPosts*.ts → src/pages/blog-post/page.tsx) ──

export type CmsBlogPost = BlogPost & { imageAlt?: string; ctaLink?: string; ctaText?: string };

export const BLOG_POST_SCHEMA: RecordSchema<CmsBlogPost> = {
  family: "blog_post",
  sectionId: "article",
  sectionLabel: "Article",
  sectionType: "content",
  description: "The article text, hero image and sections. The shared call-to-action boxes, related articles and sidebar are part of the site design.",
  fields: [
    { id: "article.title", label: "Article title (H1)", kind: "heading", where: "Top of the article, blog cards and breadcrumbs", get: (p) => p.title, set: (p, v) => ({ ...p, title: v }) },
    { id: "article.excerpt", label: "Introduction under the title", kind: "paragraph", where: "Highlighted paragraph above the first section, and blog cards", get: (p) => p.excerpt, set: (p, v) => ({ ...p, excerpt: v }) },
    { id: "article.cta_label", label: "Article button text", kind: "cta_label", get: (p) => String(p.ctaText ?? ""), set: (p, v) => ({ ...p, ctaText: v }), when: (p) => !!p.ctaLink && !!p.ctaText },
    { id: "article.cta_url", label: "Article button link", kind: "cta_url", help: "An internal page of this website, e.g. /how-to-get-esa-letter.", get: (p) => String(p.ctaLink ?? ""), set: (p, v) => ({ ...p, ctaLink: v }), when: (p) => !!p.ctaLink },
    { id: "article.author", label: "Author (managed)", kind: "paragraph", help: "Author names and credentials are managed by the clinical team.", get: (p) => p.author },
    { id: "article.date", label: "Published date (managed)", kind: "paragraph", get: (p) => p.date },
    { id: "article.category", label: "Category (managed: drives the blog filters)", kind: "paragraph", get: (p) => p.category },
  ],
  images: [
    { id: "article.image", altId: "article.image_alt", label: "Hero image", altLabel: "Hero image description (alt text)", get: (p) => ({ src: p.image, alt: p.imageAlt ?? p.title }), set: (p, img) => ({ ...p, image: img.src, imageAlt: img.alt }) },
  ],
  collections: [
    {
      id: "article.sections", label: "Article sections", kind: "steps", where: "The body of the article, in order",
      help: "Each section is a heading and its text. Add, remove or re-order sections.",
      columns: [col("heading", "Section heading", "heading"), col("content", "Section text", "paragraph")],
      get: (p) => p.sections.map((s) => ({ heading: s.heading, content: s.content })),
      set: (p, items) => ({ ...p, sections: items.map((i) => ({ heading: i.heading, content: i.content })) }),
      minItems: 1, maxItems: 30,
    },
  ],
  faqCollections: [],
};

export function cmsBlogPost<P extends BlogPost>(post: P): P {
  return patchRecord(BLOG_POST_SCHEMA as unknown as RecordSchema<P>, `/blog/${post.slug}`, post);
}

// ── automated articles (src/seo/blog/scheduledArticles.ts → scheduled-blog page) ──

export const SCHEDULED_ARTICLE_SCHEMA: RecordSchema<ScheduledArticle> = {
  family: "scheduled_article",
  sectionId: "article",
  sectionLabel: "Article",
  sectionType: "content",
  description: "An automated article. Edits made here are kept when the automation publishes later articles. The sources box and the legal note are managed.",
  fields: [
    { id: "article.title", label: "Article title (H1)", kind: "heading", where: "Top of the article, blog cards and breadcrumbs", get: (a) => a.title, set: (a, v) => ({ ...a, title: v }) },
    { id: "article.excerpt", label: "Standfirst under the title", kind: "paragraph", where: "Under the title, and blog cards", get: (a) => a.description, set: (a, v) => ({ ...a, description: v }) },
    { id: "article.date", label: "Published date (managed)", kind: "paragraph", get: (a) => a.datePublished },
  ],
  images: [
    { id: "article.image", altId: "article.image_alt", label: "Hero image", altLabel: "Hero image description (alt text)", get: (a) => ({ src: a.image, alt: a.imageAlt }), set: (a, img) => ({ ...a, image: img.src, imageAlt: img.alt }) },
  ],
  collections: [
    {
      id: "article.sections", label: "Article sections", kind: "steps", where: "The body of the article and its table of contents",
      help: "Leave a blank line between paragraphs. **bold** and [link text](/internal-page) are kept.",
      columns: [col("heading", "Section heading", "heading"), col("body", "Section text", "paragraph")],
      get: (a) => a.sections.map((s) => ({ heading: s.heading, body: s.paragraphs.join("\n\n") })),
      set: (a, items) => ({ ...a, sections: items.map((i) => ({ heading: i.heading, paragraphs: splitParas(i.body) })).filter((s) => s.paragraphs.length) }),
      minItems: 1, maxItems: 30,
    },
  ],
  faqCollections: [],
};

export function cmsScheduledArticle(article: ScheduledArticle): ScheduledArticle {
  return patchRecord(SCHEDULED_ARTICLE_SCHEMA, article.route, article);
}

/**
 * The automated article's JSON-LD with the published title, standfirst and
 * image, so the head graph matches the visible article.
 */
export function cmsScheduledGraph<G extends { "@graph": Array<Record<string, unknown>> }>(graph: G | null, article: ScheduledArticle | undefined): G | null {
  if (!graph || !article) return graph;
  const a = cmsScheduledArticle(article);
  if (a === article) return graph;
  return {
    ...graph,
    "@graph": graph["@graph"].map((node) => {
      if (node["@type"] === "BlogPosting") return { ...node, headline: a.title, description: a.description, image: `https://pawtenant.com${a.image}`.replace(/^https:\/\/pawtenant\.com(https?:\/\/)/, "$1") };
      if (node["@type"] === "BreadcrumbList" && Array.isArray(node.itemListElement)) {
        const items = node.itemListElement as Array<Record<string, unknown>>;
        return { ...node, itemListElement: items.map((it, i) => (i === items.length - 1 ? { ...it, name: a.title } : it)) };
      }
      return node;
    }),
  };
}

/** A blog card (listing, related posts, state clusters) with its article's published title, introduction and image. */
export function cmsBlogCard<P extends { slug?: string; href?: string; title: string; excerpt?: string; image?: string }>(post: P): P {
  const path = post.href ?? (post.slug ? `/blog/${post.slug}` : "");
  if (!path) return post;
  const o = cmsOverrides(path);
  if (!o) return post;
  const title = typeof o["article.title"] === "string" && o["article.title"].trim() ? o["article.title"] : null;
  const excerpt = typeof o["article.excerpt"] === "string" && o["article.excerpt"].trim() ? o["article.excerpt"] : null;
  const img = o["article.image"];
  const image = img && typeof img === "object" && !Array.isArray(img) && typeof (img as { src?: unknown }).src === "string" && /^(\/(?!\/)|https:\/\/)/.test((img as { src: string }).src) ? (img as { src: string }).src : null;
  if (!title && !excerpt && !image) return post;
  return { ...post, ...(title ? { title } : {}), ...(excerpt && post.excerpt !== undefined ? { excerpt } : {}), ...(image && post.image !== undefined ? { image } : {}) };
}

// ── ESA state pages (src/mocks/states.ts → src/pages/state-esa/page.tsx) ─────

export const STATE_ESA_SCHEMA: RecordSchema<StateData> = {
  family: "state_esa",
  sectionId: "state",
  sectionLabel: "State content",
  sectionType: "content",
  description: "The state-specific text. Prices, the evaluation steps, provider cards and the questions shared by every state page are part of the site design.",
  fields: [
    { id: "state.name", label: "State name (managed)", kind: "paragraph", get: (s) => s.name },
    { id: "state.intro", label: "Introduction", kind: "paragraph", where: "Under the page title", get: (s) => s.introText, set: (s, v) => ({ ...s, introText: v }) },
    { id: "state.laws_summary", label: "State law summary", kind: "paragraph", where: "State law section", help: "Keep statements accurate and sourced; this is legal information, not advice.", get: (s) => s.lawsSummary, set: (s, v) => ({ ...s, lawsSummary: v }) },
  ],
  images: [],
  collections: [
    { id: "state.laws", label: "State law key points", kind: "bullets", columns: [col("text", "Point", "list_item")], get: (s) => s.lawsBullets.map((text) => ({ text })), set: (s, items) => ({ ...s, lawsBullets: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "state.advantages", label: "Advantage cards", kind: "cards", help: "Re-word the cards; the number of cards is fixed by the design.", columns: [col("title", "Card title", "heading"), col("desc", "Card text", "paragraph")], get: (s) => s.advantages.map((a) => ({ title: a.title, desc: a.desc })), set: (s, items) => ({ ...s, advantages: items.map((i) => ({ title: i.title, desc: i.desc })) }), fixedLength: true },
    { id: "state.faqs", label: "State questions and answers", kind: "faq", where: "FAQ section and the page's FAQ structured data", help: "The general questions shown on every state page are shared and not edited here.", columns: [col("q", "Question", "faq_question"), col("a", "Answer", "faq_answer")], get: (s) => s.faqs.map((f) => ({ q: f.q, a: f.a })), set: (s, items) => ({ ...s, faqs: items.map((i) => ({ q: i.q, a: i.a })) }), minItems: 1, maxItems: 20 },
  ],
  faqCollections: ["state.faqs"],
};

export function cmsStateEsa<S extends StateData | undefined>(state: S): S {
  return state ? (patchRecord(STATE_ESA_SCHEMA, `/esa-letter/${state.slug}`, state) as S) : state;
}

// ── PSD state pages (src/mocks/statesPSD.ts → src/pages/state-psd/page.tsx) ──

export const STATE_PSD_SCHEMA: RecordSchema<StatePSDData> = {
  family: "state_psd",
  sectionId: "state",
  sectionLabel: "State content",
  sectionType: "content",
  description: "The state-specific PSD text. Prices, the evaluation steps and the training guidance are part of the site design. A PSD is never described as an ESA.",
  fields: [
    { id: "state.name", label: "State name (managed)", kind: "paragraph", get: (s) => s.name },
    { id: "state.laws_summary", label: "State law summary", kind: "paragraph", help: "Keep statements accurate and sourced; this is legal information, not advice.", get: (s) => s.lawsSummary, set: (s, v) => ({ ...s, lawsSummary: v }) },
  ],
  images: [],
  collections: [
    { id: "state.laws", label: "State law key points", kind: "bullets", columns: [col("text", "Point", "list_item")], get: (s) => s.lawsBullets.map((text) => ({ text })), set: (s, items) => ({ ...s, lawsBullets: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "state.advantages", label: "Advantage cards", kind: "cards", help: "Re-word the cards; the number of cards is fixed by the design.", columns: [col("title", "Card title", "heading"), col("desc", "Card text", "paragraph")], get: (s) => s.advantages.map((a) => ({ title: a.title, desc: a.desc })), set: (s, items) => ({ ...s, advantages: items.map((i) => ({ title: i.title, desc: i.desc })) }), fixedLength: true },
    { id: "state.can", label: "What a PSD letter can do", kind: "bullets", columns: [col("text", "Point", "list_item")], get: (s) => s.whatItCan.map((text) => ({ text })), set: (s, items) => ({ ...s, whatItCan: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "state.cannot", label: "What a PSD letter cannot do", kind: "bullets", columns: [col("text", "Point", "list_item")], get: (s) => s.whatItCannot.map((text) => ({ text })), set: (s, items) => ({ ...s, whatItCannot: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "state.faqs", label: "Questions and answers", kind: "faq", where: "FAQ section and the page's FAQ structured data", columns: [col("q", "Question", "faq_question"), col("a", "Answer", "faq_answer")], get: (s) => s.faqs.map((f) => ({ q: f.q, a: f.a })), set: (s, items) => ({ ...s, faqs: items.map((i) => ({ q: i.q, a: i.a })) }), minItems: 1, maxItems: 20 },
  ],
  faqCollections: ["state.faqs"],
};

export function cmsStatePsd<S extends StatePSDData | undefined>(state: S): S {
  return state ? (patchRecord(STATE_PSD_SCHEMA, `/psd-letter/${state.slug}`, state) as S) : state;
}

// ── college policy pages (src/mocks/colleges.ts → college-policy-detail page) ──

export const COLLEGE_SCHEMA: RecordSchema<College & { imageAlt?: string }> = {
  family: "college",
  sectionId: "college",
  sectionLabel: "College policy content",
  sectionType: "content",
  description: "The college's ESA housing information. Check every statement against the college's published policy. The order button and pricing are part of the site design.",
  fields: [
    { id: "college.name", label: "College name (managed)", kind: "paragraph", get: (c) => c.name },
    { id: "college.location", label: "Location (managed)", kind: "paragraph", get: (c) => c.location },
    { id: "college.esa_policy", label: "ESA policy summary", kind: "paragraph", get: (c) => c.esaPolicy, set: (c, v) => ({ ...c, esaPolicy: v }) },
    { id: "college.housing_policy", label: "Housing policy", kind: "paragraph", get: (c) => c.housingPolicy, set: (c, v) => ({ ...c, housingPolicy: v }) },
  ],
  images: [
    { id: "college.image", altId: "college.image_alt", label: "Campus image", altLabel: "Campus image description (alt text)", get: (c) => ({ src: c.image, alt: c.imageAlt ?? c.name }), set: (c, img) => ({ ...c, image: img.src, imageAlt: img.alt }) },
  ],
  collections: [
    { id: "college.key_facts", label: "Key facts", kind: "bullets", columns: [col("text", "Fact", "list_item")], get: (c) => c.keyFacts.map((text) => ({ text })), set: (c, items) => ({ ...c, keyFacts: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "college.steps", label: "Application steps", kind: "steps", columns: [col("text", "Step", "list_item")], get: (c) => c.applicationProcess.map((text) => ({ text })), set: (c, items) => ({ ...c, applicationProcess: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "college.allowed", label: "Where ESAs are allowed", kind: "bullets", columns: [col("text", "Area", "list_item")], get: (c) => c.allowedAreas.map((text) => ({ text })), set: (c, items) => ({ ...c, allowedAreas: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "college.restrictions", label: "Restrictions", kind: "bullets", columns: [col("text", "Restriction", "list_item")], get: (c) => c.restrictions.map((text) => ({ text })), set: (c, items) => ({ ...c, restrictions: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "college.faq", label: "Questions and answers", kind: "faq", where: "FAQ section and the page's FAQ structured data", columns: [col("question", "Question", "faq_question"), col("answer", "Answer", "faq_answer")], get: (c) => c.faq.map((f) => ({ question: f.question, answer: f.answer })), set: (c, items) => ({ ...c, faq: items.map((i) => ({ question: i.question, answer: i.answer })) }), minItems: 1, maxItems: 20 },
  ],
  faqCollections: ["college.faq"],
};

export function cmsCollege<C extends (College & { imageAlt?: string }) | undefined>(college: C): C {
  return college ? (patchRecord(COLLEGE_SCHEMA, `/college-pet-policy/${college.slug}`, college) as C) : college;
}

// ── state pet-rent articles (src/data/statePetRentBlogs.ts → StatePetRentBlog) ──


export const PET_RENT_SCHEMA: RecordSchema<StatePetRentBlogConfig> = {
  family: "state_pet_rent",
  sectionId: "article",
  sectionLabel: "Article",
  sectionType: "content",
  description: "The state pet-rent article. Rent figures are general estimates; the calculator, state links and reading list are part of the site design.",
  fields: [
    { id: "article.state", label: "State (managed)", kind: "paragraph", get: (c) => c.state },
    { id: "article.intro", label: "Introduction", kind: "paragraph", get: (c) => c.intro, set: (c, v) => ({ ...c, intro: v }) },
    { id: "article.rent_heading", label: "Pet rent section heading", kind: "heading", get: (c) => c.rentHeading, set: (c, v) => ({ ...c, rentHeading: v }) },
    { id: "article.law_heading", label: "State law section heading", kind: "heading", get: (c) => c.lawHeading, set: (c, v) => ({ ...c, lawHeading: v }) },
  ],
  images: [
    { id: "article.image", altId: "article.image_alt", label: "Hero image", altLabel: "Hero image description (alt text)", get: (c) => ({ src: c.heroImgPath, alt: c.heroAlt }), set: (c, img) => ({ ...c, heroImgPath: img.src, heroAlt: img.alt }) },
  ],
  collections: [
    { id: "article.summary", label: "Quick summary bullets", kind: "bullets", help: "The words before the first colon are shown in bold.", columns: [col("text", "Bullet", "list_item")], get: (c) => c.summary.map((text) => ({ text })), set: (c, items) => ({ ...c, summary: items.map((i) => i.text) }), minItems: 1, maxItems: 8 },
    { id: "article.rent_paras", label: "Pet rent section text", kind: "steps", columns: [col("text", "Paragraph", "paragraph")], get: (c) => c.rentParas.map((text) => ({ text })), set: (c, items) => ({ ...c, rentParas: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "article.law_paras", label: "State law section text", kind: "steps", help: "General educational information, not legal advice.", columns: [col("text", "Paragraph", "paragraph")], get: (c) => c.lawParas.map((text) => ({ text })), set: (c, items) => ({ ...c, lawParas: items.map((i) => i.text) }), minItems: 1, maxItems: 12 },
    { id: "article.faqs", label: "Questions and answers", kind: "faq", where: "FAQ section and the article's FAQ structured data", columns: [col("q", "Question", "faq_question"), col("a", "Answer", "faq_answer")], get: (c) => c.faqs.map((f) => ({ q: f.q, a: f.a })), set: (c, items) => ({ ...c, faqs: items.map((i) => ({ q: i.q, a: i.a })) }), minItems: 1, maxItems: 20 },
  ],
  faqCollections: ["article.faqs"],
};

export function cmsPetRentBlog(cfg: StatePetRentBlogConfig): StatePetRentBlogConfig {
  return patchRecord(PET_RENT_SCHEMA, `/blog/${cfg.slug}`, cfg);
}

// ── article records (ESA-letter cluster, housing guides, lead-magnet guides) ──
// The page module renders its own body (bound at build time, scripts/cms/autobind.mjs);
// the record holds the H1, the hero and the FAQ list that also feeds the head JSON-LD.

export interface CmsArticleRecord {
  route: string;
  h1: string;
  heroImage: string;
  heroAlt: string;
  faqs: Array<{ q: string; a: string }>;
}

export const ARTICLE_RECORD_SCHEMA: RecordSchema<CmsArticleRecord> = {
  family: "article_record",
  sectionId: "article_header",
  sectionLabel: "Title, hero and questions",
  sectionType: "hero",
  description: "The article title, hero image and its questions and answers (also used in the page's FAQ structured data). The article text is in the sections below.",
  fields: [
    { id: "article.h1", label: "Article title (H1)", kind: "heading", get: (a) => a.h1, set: (a, v) => ({ ...a, h1: v }) },
  ],
  images: [
    { id: "article.image", altId: "article.image_alt", label: "Hero image", altLabel: "Hero image description (alt text)", get: (a) => ({ src: a.heroImage, alt: a.heroAlt }), set: (a, img) => ({ ...a, heroImage: img.src, heroAlt: img.alt }) },
  ],
  collections: [
    { id: "article.faqs", label: "Questions and answers", kind: "faq", where: "FAQ section and the article's FAQ structured data", columns: [col("q", "Question", "faq_question"), col("a", "Answer", "faq_answer")], get: (a) => a.faqs.map((f) => ({ q: f.q, a: f.a })), set: (a, items) => ({ ...a, faqs: items.map((i) => ({ q: i.q, a: i.a })) }), minItems: 1, maxItems: 20 },
  ],
  faqCollections: ["article.faqs"],
};

export function cmsArticleRecord<A extends CmsArticleRecord | undefined>(article: A): A {
  return article ? (patchRecord(ARTICLE_RECORD_SCHEMA as unknown as RecordSchema<NonNullable<A>>, article.route, article as NonNullable<A>) as A) : article;
}

/**
 * The article's JSON-LD with its published H1, hero and FAQ list, so the head
 * graph keeps matching the visible page (FAQ parity).
 */
export function cmsArticleGraph<G extends Record<string, unknown> | null>(graph: G, article: CmsArticleRecord | undefined): G {
  if (!graph || !article) return graph;
  const a = cmsArticleRecord(article);
  if (a === article) return graph;
  const abs = (src: string) => (/^https?:\/\//.test(src) ? src : `https://pawtenant.com${src}`);
  const fix = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(fix);
    if (!v || typeof v !== "object") {
      if (typeof v === "string" && v === article.h1) return a.h1;
      if (typeof v === "string" && article.heroImage && v === abs(article.heroImage)) return abs(a.heroImage);
      return v;
    }
    const node = v as Record<string, unknown>;
    if (node["@type"] === "FAQPage") return { ...node, mainEntity: a.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
    return Object.fromEntries(Object.entries(node).map(([k, x]) => [k, fix(x)]));
  };
  return fix(graph) as G;
}
